import { FutureRepairs, LaborDetails, Repairs } from './repair-details';
import { TextArea, Warning, YesNoButton } from '../../../components';
import { useFormContext } from 'react-hook-form';
import { useValidation } from '../../../hooks';
import { claimFields } from '../../../config';
import { useContext, useEffect } from 'react';
import { FormPage } from './form';

import type { IClaim, IClaimInit, IRepairDetailsFormValueTypes } from '../../../types';
import type { Dispatch, FC, SetStateAction } from 'react';
import { Localization } from '../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../context/LanguageSelectorContext';

export const repairDetailsDefaultFormValues: IRepairDetailsFormValueTypes = {
    allMaterials: [],
    allPersonnel: [],
    allVehicles: [],
    [claimFields.areFutureRepairsNeeded]: null,
    [claimFields.claimComments]: '',
    [claimFields.clientConstructionNumber]: '',
    isClientConstructionNumberUnknown: false,
    isFreeFormRepairEntry: null,
    [claimFields.legacyClaimNo]: '',
    material: null,
    materialQuantity: '',
    materials: [],
    overtime: null,
    personnel: null,
    personnels: [],
    [claimFields.repairsDoneOnSite]: null,
    timeOnSite: null,
    vehicle: null,
    vehicleQuantity: '',
    vehicles: [],
};

export const repairDetailsChangesList: Array<string> = [
    'LegacyClaimNo',
    'ClaimComments',
    'RemedyPrismID',
    claimFields.areFutureRepairsNeeded,
    claimFields.repairsDoneOnSite,
    claimFields.isFreeFormRepairEntry,
];

interface IRepairDetailsProps {
    claim?: IClaimInit | IClaim | null;
    setSaveAndQuitChanges?: Dispatch<SetStateAction<Array<string>>>;
}

export const RepairDetails: FC<IRepairDetailsProps> = ({ claim, setSaveAndQuitChanges }) => {
    const formContext = useFormContext();

    const wereRepairsDoneOnSite: boolean | null = formContext.watch(claimFields.repairsDoneOnSite);
    const isFreeFormRepairEntry: boolean | null = formContext.watch(claimFields.isFreeFormRepairEntry);

    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);
    
    const { fieldRequirements } = useValidation({});

    // scroll to the top of the screen on initial render.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const onAdditionalCommentsInputChange = () => {
        setSaveAndQuitChanges?.((prev) => [...prev, 'ClaimComments', claimFields.repairsDoneOnSite]);
        formContext.setValue(claimFields.repairsDoneOnSite, true);
        formContext.clearErrors(claimFields.repairsDoneOnSite);
    };

    const onRepairDetailsInputChange = () => {
        setSaveAndQuitChanges?.((prev) => [
            ...prev,
            'ClaimComments',
            claimFields.isFreeFormRepairEntry,
            claimFields.repairsDoneOnSite,
        ]);
        formContext.setValue(claimFields.isFreeFormRepairEntry, false);
        formContext.setValue(claimFields.repairsDoneOnSite, true);
        formContext.clearErrors(claimFields.isFreeFormRepairEntry);
    };

    return (
        <FormPage>
            {/* REPAIRS */}
            <Repairs setSaveAndQuitChanges={setSaveAndQuitChanges} />

            {/* LABOR DETAILS */}
            {wereRepairsDoneOnSite !== false && (
                <>
                    <YesNoButton
                        isRequired
                        isSelected={isFreeFormRepairEntry}
                        labelText={localization.translate('repairDetails.freeFormQuestion')}
                        tooltip={localization.translate('repairDetails.freeFormTooltip')}
                        name={claimFields.isFreeFormRepairEntry}
                        onNoClick={() => {
                            setSaveAndQuitChanges?.((prev) => [
                                ...prev,
                                claimFields.repairsDoneOnSite,
                                claimFields.isFreeFormRepairEntry,
                            ]);
                            formContext.setValue(claimFields.repairsDoneOnSite, true);
                        }}
                        onYesClick={() => {
                            setSaveAndQuitChanges?.((prev) => [
                                ...prev,
                                claimFields.repairsDoneOnSite,
                                claimFields.isFreeFormRepairEntry,
                            ]);
                            formContext.setValue(claimFields.repairsDoneOnSite, true);
                        }}
                        noText={localization.translate('repairDetails.freeFormNoText')}
                        yesText={localization.translate('repairDetails.freeFormYesText')}
                    />

                    {/* LABOR DETAILS */}
                    {isFreeFormRepairEntry && <LaborDetails />}

                    {/* REPAIR DETAILS */}
                    {isFreeFormRepairEntry === false && (
                        <>
                            <Warning>
                            {localization.translate('repairDetails.repairDetailsWarning')}
                            </Warning>
                            <TextArea
                                className={'h-40'}
                                options={{
                                    isRequired: fieldRequirements.ClaimComments,
                                    label: localization.translate('repairDetails.repairDetailsLabel'),
                                    name: claimFields.claimComments,
                                    onChange: onRepairDetailsInputChange,
                                }}
                                tooltip={localization.translate('repairDetails.repairDetailsTooltip')}
                            />
                        </>
                    )}

                    {/* Additional Comments */}
                    {isFreeFormRepairEntry && (
                        <TextArea
                            options={{
                                isRequired: fieldRequirements.ClaimComments,
                                label: localization.translate('repairDetails.additionalCommentsLabel'),
                                name: claimFields.claimComments,
                                onChange: onAdditionalCommentsInputChange,
                            }}
                            tooltip={localization.translate('repairDetails.additionalCommentsTooltip')}
                        />
                    )}
                </>
            )}

            {/* FUTURE REPAIRS */}
            <FutureRepairs
                altName={claim?.clientConstructionNumberAltName}
                setSaveAndQuitChanges={setSaveAndQuitChanges}
            />
        </FormPage>
    );
};